'use client';

import { useEffect } from 'react';
import {
  getCookiesLocationDetails,
  setCurrentLoaction,
} from '@/lib/cookies/getCookiesLocationDetails';
import { useRouter } from '@/i18n/routing';
import { AddressTypeStrEnum } from '@/types/address';

type TLatLon = {
  latitude: number;
  longitude: number;
};

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

// Define type for Geometry
interface Geometry {
  location: {
    lat: number;
    lng: number;
  };
}

// Define type for the Result object returned by Google Geocoding API
interface GeocodingResult {
  formatted_address: string;
  address_components: AddressComponent[];
  geometry: Geometry;
}

// Define the expected structure for the data returned by the API
interface GeocodingApiResponse {
  results: GeocodingResult[];
}

// Function to fetch location details from Google Geocoding API
const fetchLocationDetailsFromGoogle = async ({ latitude, longitude }: TLatLon) => {
  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&language=en`,
  );
  if (!response.ok) {
    throw new Error('Failed to fetch location details from Google Geocoding API');
  }
  return response.json();
};

// Function to set location details in cookies
const setLocationCookies = (data: GeocodingApiResponse): void => {
  const results = data?.results?.[0] || ({} as GeocodingResult);

  const fullAddressText = results?.formatted_address || '';
  const location = {
    latitude: results.geometry?.location.lat,
    longitude: results.geometry?.location.lng,
  };

  const addressComponents = results?.address_components || [];

  const building =
    addressComponents.find((component: AddressComponent) =>
      component.types?.includes('street_number'),
    )?.long_name || '';

  const street =
    addressComponents.find((component: AddressComponent) => component.types?.includes('route'))
      ?.long_name || '';

  const area =
    addressComponents.find((component: AddressComponent) =>
      component.types?.includes('sublocality'),
    )?.long_name || '';

  const city =
    addressComponents.find(
      (component: AddressComponent) =>
        component.types?.includes('locality') ||
        component.types?.includes('administrative_area_level_2'),
    )?.long_name || '';

  const province =
    addressComponents.find((component: AddressComponent) =>
      component.types?.includes('administrative_area_level_1'),
    )?.long_name || '';

  const country =
    addressComponents.find((component: AddressComponent) => component.types?.includes('country'))
      ?.long_name || '';

  const zipCode =
    addressComponents.find((component: AddressComponent) =>
      component.types?.includes('postal_code'),
    )?.long_name || '';

  const nameAddress: string = `${building} ${street} ${area} ${city} `;

  setCurrentLoaction({
    location,
    name: street || nameAddress || fullAddressText,
    details: fullAddressText || nameAddress,
    city,
    country,
    type: 3,
    typeArabicName: AddressTypeStrEnum.Other,
    typeEnglishName: AddressTypeStrEnum.Other,
    street,
    province,
    zip_code: zipCode,
  });
};

// Function to fetch location using Google Geolocation API
const fetchGoogleGeolocation = async () => {
  const response = await fetch(
    `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`, // Replace with your API key
    {
      method: 'POST',
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch location from Google Geolocation API');
  }
  const data = await response.json();

  return { latitude: data.location.lat, longitude: data.location.lng };
};

// Main Component
const CurrentLocationComponent = () => {
  const cookieLocation = getCookiesLocationDetails();
  const router = useRouter();

  useEffect(() => {
    // If location data already exists in cookies, no need to fetch
    if (cookieLocation) return;

    // Fetch user's current location using Geolocation API
    const fetchAndSetLocation = async () => {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser.');
        try {
          // Fallback to Google Geolocation API if browser-based geolocation is not supported
          const googleLocationData = await fetchGoogleGeolocation();
          const locationData = await fetchLocationDetailsFromGoogle(googleLocationData);
          setLocationCookies(locationData);
          router.refresh();
        } catch (error) {
          console.error('Error fetching location from Google Geolocation API:', error);
        }
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const locationData = await fetchLocationDetailsFromGoogle({ latitude, longitude });
            setLocationCookies(locationData);
            router.refresh();
          } catch (error) {
            console.error('Error fetching location details:', error);
          }
        },
        async (error) => {
          console.error('Error obtaining geolocation:', error);
          try {
            // Fallback to Google Geolocation API if user denies browser-based geolocation
            const googleLocationData = await fetchGoogleGeolocation();
            const locationData = await fetchLocationDetailsFromGoogle(googleLocationData);
            setLocationCookies(locationData);
            router.refresh();
          } catch (error) {
            console.error('Error fetching location from Google Geolocation API:', error);
          }
        },
      );
    };

    fetchAndSetLocation();
  }, [cookieLocation, router]);

  return null; // No visual output needed for this component
};

export default CurrentLocationComponent;
